// Pure geometry helpers used by the Live Map to place, orient and contextualize trucks
export type LatLngLiteral = { lat: number; lng: number };

const EARTH_RADIUS_METERS = 6371000;

/**
 * Convert degrees to radians.
 * @param {number} degrees
 * @returns {number}
 */
const toRadians = (degrees: number) => (degrees * Math.PI) / 180;

/**
 * Convert radians to degrees.
 * @param {number} radians
 * @returns {number}
 */
const toDegrees = (radians: number) => (radians * 180) / Math.PI;

/**
 * Great-circle distance between two points using the haversine formula.
 * @param {LatLngLiteral} from
 * @param {LatLngLiteral} to
 * @returns {number} Distance in meters.
 */
export function getDistanceMeters(
  from: LatLngLiteral,
  to: LatLngLiteral
): number {
  const dLat = toRadians(to.lat - from.lat);
  const dLng = toRadians(to.lng - from.lng);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRadians(from.lat)) *
      Math.cos(toRadians(to.lat)) *
      Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_RADIUS_METERS * Math.asin(Math.min(1, Math.sqrt(a)));
}

/**
 * Initial compass bearing to travel from one point to another.
 * @param {LatLngLiteral} from
 * @param {LatLngLiteral} to
 * @returns {number} Bearing in degrees clockwise from north, in the range [0, 360).
 */
export function getBearingDegrees(
  from: LatLngLiteral,
  to: LatLngLiteral
): number {
  const fromLat = toRadians(from.lat);
  const toLat = toRadians(to.lat);
  const dLng = toRadians(to.lng - from.lng);
  const y = Math.sin(dLng) * Math.cos(toLat);
  const x =
    Math.cos(fromLat) * Math.sin(toLat) -
    Math.sin(fromLat) * Math.cos(toLat) * Math.cos(dLng);
  return (toDegrees(Math.atan2(y, x)) + 360) % 360;
}

// Ignore position changes smaller than this when deriving a heading, so GPS
// jitter while a truck is parked doesn't spin its arrow around.
export const MIN_HEADING_DISTANCE_METERS = 15;

/**
 * Derive the direction a truck is moving from its recent positions, newest last.
 * Walks back from the latest point until it finds one far enough away to give a
 * stable bearing.
 * @param {LatLngLiteral[]} path - Positions ordered oldest to newest.
 * @param {number} [minDistanceMeters=MIN_HEADING_DISTANCE_METERS]
 * @returns {number | null} Bearing in degrees, or null when the truck hasn't moved enough to tell.
 */
export function getHeadingFromPath(
  path: LatLngLiteral[],
  minDistanceMeters: number = MIN_HEADING_DISTANCE_METERS
): number | null {
  const latest = path[path.length - 1];
  if (!latest) return null;
  for (let idx = path.length - 2; idx >= 0; idx--) {
    const previous = path[idx];
    if (
      !!previous &&
      getDistanceMeters(previous, latest) >= minDistanceMeters
    ) {
      return getBearingDegrees(previous, latest);
    }
  }
  return null;
}

/**
 * Whether a point lies inside a polygon (ray casting on the lat/lng plane, which
 * is accurate enough for site-sized geofences).
 * @param {LatLngLiteral} point
 * @param {LatLngLiteral[]} polygon - Vertices in order; the closing edge is implied.
 * @returns {boolean}
 */
export function isPointInPolygon(
  point: LatLngLiteral,
  polygon: LatLngLiteral[]
): boolean {
  let isInside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const a = polygon[i];
    const b = polygon[j];
    if (!a || !b) continue;
    const crossesRay =
      a.lat > point.lat !== b.lat > point.lat &&
      point.lng <
        ((b.lng - a.lng) * (point.lat - a.lat)) / (b.lat - a.lat) + a.lng;
    if (crossesRay) isInside = !isInside;
  }
  return isInside;
}
